/*!
 * Adapted from "The Furrow - ASCIIxGPT - Concept 5" by The Furrow
 * (https://thefurrow.tv/project/asciixgpt). Changes: ported to a WebGL2 shader,
 * with a different pattern set, glyphs, colors, settings, and a carve around content.
 * This file is licensed under CC BY-NC-SA 4.0:
 * https://creativecommons.org/licenses/by-nc-sa/4.0/
 */

// The field: a WebGL2 pattern of glyphs around the text column, plus its wisps and the waves'
// colors. atlas.ts draws the glyph sprites, gpu.ts sets up the programs, and carve.ts holds the
// shape of the clear column. This file keeps them all in step with the page.

import type { GrainGrid } from "@bfly/engine";
import type { WaveRing } from "../waves/waves.js";
import type { WaveAges } from "../waves/wave-ages.js";
import { AgesReadback } from "./ages-readback.js";
import { FieldPalettes } from "./field-palettes.js";
import { CHARSET, FONT_FAMILY, FONT_SIZE, GLYPHS, TIER_REPEAT, drawAtlas } from "./atlas.js";
import { carveOutside } from "./carve.js";
import { CarveMeasure } from "./carve-measure.js";
import type { Painting } from "../waves/palette-waves.js";
import {
  FRAME_MULTIPLIER,
  MAX_BREAKOUTS,
  MAX_RINGS,
  TWINKLE_GLYPHS,
  patternIndex,
} from "./field-shaders.js";
import { setupGpu, type FieldGpu } from "./gpu.js";
import { PARALLAX, ParallaxSpring } from "./parallax.js";
import {
  WAKE_SAMPLES,
  WAKE_THICK,
  WISP_MIN_VIEWPORT,
  WISP_SLOTS,
  createWisps,
  type WispEnvironment,
} from "./wisps.js";
import { motionReduced, onMotionChange } from "../page/motion.js";
import { currentTheme, onThemeChange } from "../page/theme.js";
import { pageScrollY } from "../page/page-scroll.js";
import type { FieldDust } from "../dust/field-dust.js";
import { DUST_CELL_SIZE, DUST_DURATION_SECONDS, dustFade } from "../dust/dust-sweep.js";

// The field's time moves `FRAME_MULTIPLIER * SPEED` every `FRAME_MS` milliseconds. The pattern's
// shape also uses `FRAME_MULTIPLIER`, so `SPEED` only changes the motion.
const FRAME_MS = 100;
const SPEED = 0.15;
// Twinkles per second per 1000 cells of the edge band (the only place they show up): about 2.5
// a second at 1440 x 900.
const TWINKLE_RATE = 2;
// Things that keep their own clicks, even over a wisp: the page's controls.
const CONTROLS =
  "a, button, input, select, textarea, label, summary, jrsm-video, [tabindex], [role='button'], [role='slider'], [role='switch']";
// The gust's grains under each live wave (gust-grains.ts in the engine): they swell the glyphs
// where dust is in the air, blend in a wave's palette, and tell the rest of the page each cell's
// age. They've been off since the dust sweep (dust/field-dust.ts) took over repainting the
// field, but the code stays so they can come back. While they're off, everything follows the
// fallback front (core/gust.ts), wisps included.
const GUST_GRAINS = false;

export interface FieldOptions {
  // Below this element's top, the carve narrows like the end of an egg (`CAP_TAPER`).
  taper: HTMLElement | null;
  // Content wider than the carve's column, which the field keeps clear too.
  breakouts: readonly HTMLElement[];
}

export interface Field {
  // Every live wave's ring in viewport CSS pixels, oldest first (an empty list once they've all
  // ended), plus the palettes they paint (palettes.ts). The caller sends them every frame, so
  // the rings move at the display's frame rate while the field keeps its own steps. Past
  // `MAX_RINGS`, the newest ones win.
  setWaves(rings: readonly WaveRing[], painting: Painting): void;
  /** Each live wave's age at each cell (wave-ages.ts), as the field last read them, so a frame
      or more late. `NO_AGES` when no wave is live. Null once the field has stopped (a lost
      context) and while the grains are off, so the rest of the page follows the fallback
      front. */
  ages(): WaveAges | null;
  /** Turns the field's next frame to dust, which blows away from `origin` (the butterfly, in
      viewport CSS pixels). `change` runs as the frame is copied, so the field repaints under
      the dust. Settles once the dust has gone, or right away (after `change`) when no dust can
      run. */
  sweep(origin: { x: number; y: number }, change: () => void): Promise<void>;
}

interface FieldContexts {
  gl: WebGL2RenderingContext;
  // The glyph sprites (atlas.ts).
  atlas: HTMLCanvasElement;
  atlasCtx: CanvasRenderingContext2D;
  // The page background, which the wisps invert against. A 2D context turns any CSS color,
  // `oklch()` included, into sRGB.
  groundCtx: CanvasRenderingContext2D;
}

function fieldContexts(canvas: HTMLCanvasElement): FieldContexts | null {
  const gl = canvas.getContext("webgl2", { antialias: false });
  const atlas = document.createElement("canvas");
  // Both 2D canvases get read back: the atlas as a texture upload, the ground as a pixel. They
  // live in CPU memory, so a read doesn't stall the GPU.
  const atlasCtx = atlas.getContext("2d", { willReadFrequently: true });
  const groundCtx = document.createElement("canvas").getContext("2d", { willReadFrequently: true });
  return gl && atlasCtx && groundCtx ? { gl, atlas, atlasCtx, groundCtx } : null;
}

// The field fills `canvas` and leaves the carve element clear. Each breakout element stays clear
// too, padded the same way the carve pads its content; a page has at most `MAX_BREAKOUTS` (a CI
// test checks). Resolves to null when WebGL2 isn't available, the setup fails, or `signal` (the
// element's connection, elements/jrsm-glyph-field.ts) ends first.
export async function startField(
  canvas: HTMLCanvasElement,
  carve: HTMLElement,
  { taper, breakouts }: FieldOptions,
  signal: AbortSignal,
): Promise<Field | null> {
  const contexts = fieldContexts(canvas);
  // Without WebGL2 the canvas just stays empty, and the content still reads fine.
  if (!contexts) return null;
  const { gl, atlas, atlasCtx, groundCtx } = contexts;

  // The first frame waits for the glyph font too: until it's ready, a canvas draws with a
  // fallback font, and the glyphs would change after the page shows. The page preloads the
  // font, so it's usually ready first.
  const font = document.fonts
    .load(`${FONT_SIZE}px ${FONT_FAMILY}`, [...CHARSET, ...TWINKLE_GLYPHS].join(""))
    .catch((error: unknown) => console.warn("field: the glyph font did not load", error));
  // If the field leaves the page mid-setup, it releases its context and draws nothing.
  const loseContext = (): void => gl.getExtension("WEBGL_lose_context")?.loseContext();
  // A shader that doesn't compile or link, or any other setup error, leaves the field empty, as
  // if there were no WebGL2, so the rest of the page still starts. The field releases its
  // context along with whatever part of the setup got made (the grains' objects when a field
  // program failed, or the other way around).
  let gpu: FieldGpu;
  try {
    [gpu] = await Promise.all([setupGpu(gl), font]);
  } catch (error) {
    if (!signal.aborted) console.warn("Field: not drawn", error);
    loseContext();
    return null;
  }
  if (signal.aborted) {
    loseContext();
    return null;
  }
  const { liftProgram, l, program, u, liftFramebuffer, agesFramebuffer, grains } = gpu;

  const wideEnough = matchMedia(`(min-width: ${WISP_MIN_VIEWPORT}px)`);
  let dpr = 1;
  let time = 0;
  let lastStepAt = 0;
  let lost = false;
  // Wave and scroll updates mark the field dirty, and the next tick draws once for all of them.
  // Without the loop (reduced motion) no tick comes, so they draw right away.
  let looping = false;
  let dirty = false;
  // The live waves (`setWaves`), the newest `MAX_RINGS`, in the form the lift pass wants: each
  // one's grain layer, its simulation time, and its strength.
  const liveRings: WaveRing[] = [];
  const ringSlot = new Int32Array(MAX_RINGS);
  const ringTime = new Float32Array(MAX_RINGS);
  const ringStrength = new Float32Array(MAX_RINGS);
  // The grid and the viewport in CSS pixels, for the grains (`layout()`).
  const grainGrid: GrainGrid = { cols: 1, rows: 1, cellW: 1, cellH: 1, originX: 0, originY: 0 };
  const viewport = { width: 0, height: 0 };
  // The ages, read back from the GPU a frame or more late, and the palettes.
  const readback = new AgesReadback(gl, liftFramebuffer, agesFramebuffer);
  const palettes = new FieldPalettes(gl, u);
  let ringCount = 0;
  // The bands' scroll offset (parallax.ts), in CSS pixels. With reduced motion the bands hold
  // still in the viewport, since parallax is too much motion for some people. The tick reads the
  // scroll position as the last scroll event left it (page-scroll.ts): it can run after another
  // part's frame wrote styles, and a live read would force a layout.
  const parallax = new ParallaxSpring();
  const parallaxTarget = (): number => (motionReduced() ? 0 : PARALLAX * pageScrollY());
  let raf = 0;
  let lastFrameAt = 0;
  // The clock for the wisps and the twinkles, in seconds. It stops when the loop does, so a
  // hidden tab doesn't age the wisps: they pick up where they left off.
  let stoppedMs = 0;
  let stoppedAt: number | null = null;
  const clock = (ms = performance.now()): number => ((stoppedAt ?? ms) - stoppedMs) / 1000;
  // The cell grid, in device pixels (`layout()`).
  const grid: {
    cellW: number;
    cellH: number;
    cols: number;
    rows: number;
    originX: number;
    originY: number;
  } = { cellW: 1, cellH: 1, cols: 1, rows: 1, originX: 0, originY: 0 };
  // The carve and the breakouts, measured on a scroll or a layout change.
  const carveBoxes = new CarveMeasure(carve, taper, breakouts);
  const { shape } = carveBoxes;
  const breakoutData = new Float32Array(MAX_BREAKOUTS * 4);
  const wisps = createWisps();

  // The dust sweep (dust/field-dust.ts), in this same context. Its module loads and its programs
  // link after the field's own. A request waits for the next draw, which copies that frame and
  // runs the request's change; then the dust draws over each frame until it's gone. Anything
  // that ends it early (a new size, a stopped loop, a lost context) still runs the change and
  // settles the request, so the page's hold on waves always ends.
  let dust: FieldDust | null = null;
  const dustLoading = import("../dust/field-dust.js")
    .then(({ FieldDust }) => FieldDust.create(gl, signal))
    .then(
      (created) => (dust = created),
      (error: unknown) => {
        if (!signal.aborted) console.warn("field: no dust", error);
        return null;
      },
    );
  let dustRequest: { origin: { x: number; y: number }; change: () => void } | null = null;
  let dustStartedAt: number | null = null;
  let dustDone: (() => void) | null = null;
  // A grain's side in device pixels, same as the old site: 4px of a capture at up to 1.25
  // device pixels per CSS pixel.
  const dustCell = (): number => (DUST_CELL_SIZE / Math.min(dpr, 1.25)) * dpr;
  function endDust(): void {
    dustStartedAt = null;
    const request = dustRequest;
    dustRequest = null;
    request?.change();
    const done = dustDone;
    dustDone = null;
    done?.();
  }
  // After the field's pass: copy a requested frame, or draw the dust over the frame.
  function drawDust(): void {
    if (!dust) return;
    if (dustRequest) {
      const { origin, change } = dustRequest;
      dustRequest = null;
      // The dark theme blends the canvas in difference mode, where black changes nothing.
      const neutral = currentTheme() === "dark" ? ([0, 0, 0] as const) : readGround();
      dust.capture(
        origin.x / canvas.clientWidth,
        origin.y / canvas.clientHeight,
        dustCell(),
        neutral,
      );
      dustStartedAt = performance.now();
      change();
      return;
    }
    if (dustStartedAt === null) return;
    const elapsed = (performance.now() - dustStartedAt) / 1000;
    dust.render(elapsed, dustFade(elapsed));
    if (elapsed >= DUST_DURATION_SECONDS) endDust();
  }
  const dusting = (): boolean => dustRequest !== null || dustStartedAt !== null;

  function layout(): void {
    // A new size clears the drawing buffer, and the dust's frame no longer fits.
    if (dusting()) endDust();
    dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(canvas.clientWidth * dpr);
    canvas.height = Math.round(canvas.clientHeight * dpr);
    gl.viewport(0, 0, canvas.width, canvas.height);

    const cell = drawAtlas(atlas, atlasCtx, dpr);
    const cols = Math.ceil(canvas.width / cell.width);
    const rows = Math.ceil(canvas.height / cell.height);
    // The default context composites premultiplied alpha. Only the atlas gets premultiplied:
    // the wake rings' alpha is a height, not coverage.
    gl.activeTexture(gl.TEXTURE0);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, atlas);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);

    // One texel per cell: the lift, the ages, the palette weights, and the ages' copy (a new
    // size cancels any read of it in flight).
    for (const unit of [gl.TEXTURE2, gl.TEXTURE4, gl.TEXTURE5, gl.TEXTURE6]) {
      gl.activeTexture(unit);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, cols, rows, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    }
    readback.drop();

    grid.cellW = cell.width;
    grid.cellH = cell.height;
    grid.cols = cols;
    grid.rows = rows;
    grid.originX = Math.floor((canvas.width - cols * cell.width) / 2);
    grid.originY = Math.floor((canvas.height - rows * cell.height) / 2);
    grainGrid.cols = cols;
    grainGrid.rows = rows;
    grainGrid.cellW = cell.width / dpr;
    grainGrid.cellH = cell.height / dpr;
    grainGrid.originX = grid.originX / dpr;
    grainGrid.originY = grid.originY / dpr;
    // A resize can come right after the dust drew, with its program still bound.
    gl.useProgram(program);
    gl.uniform1i(u.charsetLen, CHARSET.length);
    gl.uniform2i(u.tiers, GLYPHS.length, TIER_REPEAT);
    gl.uniform1f(u.height, canvas.height);
    gl.uniform2f(u.cell, cell.width, cell.height);
    gl.uniform1f(u.pad, cell.pad);
    gl.uniform2f(u.origin, grid.originX, grid.originY);
    gl.uniform2f(u.grid, cols, rows);
    // The grid in CSS pixels, for things on the page that line up with it: the corner plates
    // (the `corner-plate` mixin in _tokens.scss). The grid is centered, so its first and last
    // columns and its last row can hang past the viewport.
    const root = document.documentElement.style;
    root.setProperty("--field-cell-width", `${cell.width / dpr}px`);
    root.setProperty("--field-cell-height", `${cell.height / dpr}px`);
    root.setProperty("--field-overhang-left", `${-grid.originX / dpr}px`);
    root.setProperty(
      "--field-overhang-right",
      `${(grid.originX + cols * cell.width - canvas.width) / dpr}px`,
    );
    root.setProperty(
      "--field-overhang-bottom",
      `${(grid.originY + rows * cell.height - canvas.height) / dpr}px`,
    );

    carveBoxes.readStyles();
    viewport.width = canvas.clientWidth;
    viewport.height = canvas.clientHeight;
    wispEnvironment.width = viewport.width;
    wispEnvironment.height = viewport.height;
    measureCarve();
  }

  function measureCarve(): void {
    carveBoxes.measure(canvas.clientWidth, (carveBoxes.edgeRows * grid.cellH) / dpr, 1 / dpr);
  }

  function readGround(): [number, number, number] {
    groundCtx.fillStyle = getComputedStyle(document.body).backgroundColor;
    groundCtx.fillRect(0, 0, 1, 1);
    const { data } = groundCtx.getImageData(0, 0, 1, 1);
    return [(data[0] ?? 0) / 255, (data[1] ?? 0) / 255, (data[2] ?? 0) / 255];
  }

  // The theme's ground color, and twinkles if motion allows them. CSS sets the canvas's blend
  // mode per theme.
  function applySettings(): void {
    // A theme or motion change can come right after the dust drew, with its program still bound.
    gl.useProgram(program);
    gl.uniform3f(u.ground, ...readGround());
    gl.uniform1f(u.twinkleRate, motionReduced() ? 0 : TWINKLE_RATE);
  }

  // What the wisps need to know about the field, in viewport CSS pixels, updated in place
  // (`updateWispEnvironment()`).
  const wispEnvironment: WispEnvironment = {
    width: 0,
    height: 0,
    carveCenterX: 0,
    band: 1,
    // Same as the shader's `carveEdge()`.
    carveEdge: (x, y) => carveOutside(x, y, shape) / carveBoxes.band,
    cellAt(x, y) {
      const cellX = Math.floor((x * dpr - grid.originX) / grid.cellW);
      const cellY = Math.floor((y * dpr - grid.originY) / grid.cellH);
      return {
        x: cellX,
        y: cellY,
        cx: (grid.originX + (cellX + 0.5) * grid.cellW) / dpr,
        cy: (grid.originY + (cellY + 0.5) * grid.cellH) / dpr,
      };
    },
    // Picked the way the shader does it: the index's whole part, clamped to the charset.
    glyphAt(cell) {
      const index = Math.floor(patternIndex(cell.x, cell.y + shiftRows(), time, CHARSET.length));
      return CHARSET[Math.min(CHARSET.length - 1, Math.max(0, index))] ?? "";
    },
    time: 0,
    patternShift: 0,
  };

  function updateWispEnvironment(): void {
    wispEnvironment.carveCenterX = (shape.column.left + shape.column.right) / 2;
    wispEnvironment.band = carveBoxes.band;
    wispEnvironment.time = time;
    wispEnvironment.patternShift = shiftRows();
  }

  // The bands' offset, in grid rows.
  function shiftRows(): number {
    return (parallax.position * dpr) / grid.cellH;
  }

  function draw(): void {
    if (lost) return;
    // The waves the grains carry: none while they're off (`GUST_GRAINS`).
    const grainRings = GUST_GRAINS ? ringCount : 0;
    if (grainRings > 0) readback.collect();
    else readback.clear();
    const packed = wisps.pack(clock(), dpr);

    // The grains: each live wave's simulation steps to its time and marks its layer.
    if (GUST_GRAINS) {
      grains.update(liveRings, grainGrid, viewport);
      for (let i = 0; i < liveRings.length; i++) {
        const id = liveRings[i]!.id;
        ringSlot[i] = Math.max(0, grains.slotOf(id));
        ringTime[i] = grains.timeOf(id);
      }
    }

    // Pass 1: each cell's lift.
    gl.useProgram(liftProgram);
    gl.uniform2f(l.cell, grid.cellW, grid.cellH);
    gl.uniform2f(l.origin, grid.originX, grid.originY);
    gl.uniform2i(l.grainOffset, grains.offset.x, grains.offset.y);
    gl.uniform1iv(l.ringSlot, ringSlot);
    gl.uniform1fv(l.ringTime, ringTime);
    gl.uniform1fv(l.ringStrength, ringStrength);
    gl.uniform1i(l.ringCount, grainRings);
    gl.uniform4fv(l.wisp, packed.wisp);
    gl.uniform4fv(l.wakeBox, packed.box);
    gl.uniform1f(l.wakeThick, WAKE_THICK * dpr);
    gl.activeTexture(gl.TEXTURE1);
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      0,
      0,
      WAKE_SAMPLES,
      WISP_SLOTS,
      gl.RGBA,
      gl.FLOAT,
      packed.wake,
    );
    gl.bindFramebuffer(gl.FRAMEBUFFER, liftFramebuffer);
    gl.viewport(0, 0, grid.cols, grid.rows);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    if (grainRings > 0) readback.fence(liveRings, grid.cols, grid.rows, grainGrid);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, canvas.width, canvas.height);

    // Pass 2: the field.
    gl.useProgram(program);
    const { column } = shape;
    gl.uniform4f(
      u.box,
      column.left * dpr,
      column.top * dpr,
      column.right * dpr,
      column.bottom * dpr,
    );
    gl.uniform2f(u.cap, shape.capTop * dpr, shape.capBottom * dpr);
    for (let i = 0; i < shape.breakouts.length; i++) {
      const box = shape.breakouts[i];
      if (!box) continue;
      breakoutData[i * 4] = box.left * dpr;
      breakoutData[i * 4 + 1] = box.top * dpr;
      breakoutData[i * 4 + 2] = box.right * dpr;
      breakoutData[i * 4 + 3] = box.bottom * dpr;
    }
    gl.uniform4fv(u.breakouts, breakoutData);
    gl.uniform1i(u.breakoutCount, shape.breakouts.length);
    gl.uniform1f(u.band, carveBoxes.band * dpr);
    gl.uniform1f(u.time, time);
    gl.uniform1f(u.patternShift, shiftRows());
    gl.uniform1f(u.clock, clock());
    gl.uniform1i(u.ringCount, grainRings);
    palettes.upload(currentTheme());
    gl.uniform4fv(u.wisp, packed.wisp);
    gl.uniform2fv(u.wispSource, packed.source);
    gl.uniform1fv(u.wispTime, packed.time);
    gl.uniform4fv(u.wakeBox, packed.box);
    gl.uniform3fv(u.trail, packed.trail);
    gl.uniform4fv(u.trailBox, packed.trailBox);
    gl.uniform4fv(u.pop, packed.pop);
    gl.uniform1fv(u.popSpin, packed.popSpin);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    drawDust();
  }

  // The field's time moves in fixed steps every `FRAME_MS`, not with the wall clock, so the
  // pattern moves evenly. While wisps are alive, the field draws every frame so they swim
  // smoothly.
  function tick(now: number): void {
    if (lost) return;
    // A hidden tab pauses the loop, and the cap keeps a long frame from flinging the wisps.
    const dt = Math.min(0.05, Math.max(0, (now - lastFrameAt) / 1000));
    lastFrameAt = now;
    let changed = false;
    if (now - lastStepAt >= FRAME_MS) {
      // At most one step per tick, so a hidden tab doesn't jump ahead when it comes back.
      lastStepAt = Math.max(lastStepAt + FRAME_MS, now - FRAME_MS);
      time += FRAME_MULTIPLIER * SPEED;
      changed = true;
    }
    if (parallax.step(parallaxTarget(), dt)) changed = true;
    if (wideEnough.matches) {
      updateWispEnvironment();
      if (wisps.update(clock(now), dt, wispEnvironment)) changed = true;
      if (pointer) showWispHand();
    }
    // The dust moves every frame.
    if (changed || dirty || dusting()) {
      dirty = false;
      draw();
    }
    raf = requestAnimationFrame(tick);
  }

  // Where the pointer is over the field, in viewport CSS pixels, or null over a control or
  // outside the window. The hand cursor shows while a wisp is under it, including when a wisp
  // swims in under a still pointer (`tick`).
  let pointer: { x: number; y: number } | null = null;
  function showWispHand(): void {
    document.documentElement.toggleAttribute(
      "data-wisp",
      pointer !== null && wisps.hit(pointer.x, pointer.y),
    );
  }

  function redraw(): void {
    if (looping) dirty = true;
    else draw();
  }

  function play(): void {
    if (lost) return;
    cancelAnimationFrame(raf);
    // Reduced motion turns off the twinkles (`applySettings`) and the wisps. A hidden tab just
    // freezes them where they are. Without the loop, waves and scrolls draw right away.
    const reduced = motionReduced();
    if (reduced) {
      wisps.clear();
      parallax.settle(0);
    }
    looping = !reduced && !document.hidden;
    // The dust only moves in the loop.
    if (!looping && dusting()) endDust();
    if (looping && stoppedAt !== null) {
      stoppedMs += performance.now() - stoppedAt;
      stoppedAt = null;
    } else if (!looping) stoppedAt ??= performance.now();
    dirty = false;
    applySettings();
    measureCarve();
    draw();
    if (!looping) return;
    lastStepAt = performance.now();
    lastFrameAt = lastStepAt;
    raf = requestAnimationFrame(tick);
  }

  // A lost context can't draw again during this visit, so the field stops.
  canvas.addEventListener(
    "webglcontextlost",
    () => {
      lost = true;
      cancelAnimationFrame(raf);
      readback.lose();
      endDust();
    },
    { once: true, signal },
  );
  const sized = new ResizeObserver(() => {
    if (lost) return;
    layout();
    draw();
  });
  sized.observe(canvas);
  const carved = new ResizeObserver(() => {
    measureCarve();
    draw();
  });
  carved.observe(carve);
  // Scroll events come before the frame's animation callbacks, so the next tick draws the
  // carve where the content is now.
  window.addEventListener(
    "scroll",
    () => {
      measureCarve();
      redraw();
    },
    { passive: true, signal },
  );
  onMotionChange(play, { signal });
  document.addEventListener("visibilitychange", play, { signal });
  // The theme changes the palette and the ground.
  onThemeChange(
    () => {
      if (lost) return;
      applySettings();
      palettes.themeChanged();
      draw();
    },
    { signal },
  );
  // Narrowing below `WISP_MIN_VIEWPORT` ends the wisps at once; widening starts fresh ones.
  wideEnough.addEventListener(
    "change",
    () => {
      if (wideEnough.matches) return;
      wisps.clear();
      draw();
    },
    { signal },
  );
  // Clicking a wisp pops it (`pop` in wisps.ts), and a wisp under the pointer shows the hand
  // cursor (`data-wisp`, in site.scss). The canvas ignores pointer input, so the window listens;
  // a control under the pointer keeps its own clicks.
  const root = document.documentElement;
  const onField = (event: PointerEvent): boolean =>
    looping && !(event.target instanceof Element && event.target.closest(CONTROLS));
  window.addEventListener(
    "pointermove",
    (event) => {
      pointer = onField(event) ? { x: event.clientX, y: event.clientY } : null;
      showWispHand();
    },
    { passive: true, signal },
  );
  // A pointer that leaves the window is over no wisp.
  document.addEventListener(
    "pointerout",
    (event) => {
      if (event.relatedTarget !== null) return;
      pointer = null;
      showWispHand();
    },
    { signal },
  );
  window.addEventListener(
    "pointerdown",
    (event) => {
      if (!event.isPrimary || event.button !== 0 || !onField(event)) return;
      if (!wisps.pop(event.clientX, event.clientY, clock())) return;
      // The click belonged to the pop, so it doesn't start a text selection.
      event.preventDefault();
      root.removeAttribute("data-wisp");
      redraw();
    },
    { signal },
  );
  signal.addEventListener("abort", () => root.removeAttribute("data-wisp"), { once: true });

  // The element's disconnect: the loop, the listeners, and the observers stop, and the context
  // is released along with its GPU memory.
  signal.addEventListener(
    "abort",
    () => {
      lost = true;
      cancelAnimationFrame(raf);
      readback.lose();
      endDust();
      dust?.dispose();
      for (const observer of [sized, carved]) observer.disconnect();
      loseContext();
    },
    { once: true },
  );

  // A page that opens scrolled (a reload) starts with the bands at rest.
  parallax.settle(parallaxTarget());
  layout();
  play();

  // No grains, no ages: the rest of the page follows the fallback front.
  const fieldAges = (): WaveAges | null => (lost || !GUST_GRAINS ? null : readback.ages);

  return {
    ages: fieldAges,
    async sweep(origin, change) {
      await dustLoading;
      // With no dust programs, a lost context, no loop (reduced motion, a hidden tab), or a
      // sweep already running, the field just repaints with no dust.
      if (!dust || lost || !looping || dusting()) {
        change();
        return;
      }
      const { promise, resolve } = Promise.withResolvers<void>();
      dustRequest = { origin, change };
      dustDone = resolve;
      return promise;
    },
    setWaves(rings, painting) {
      // The waves pop the wisps that their fronts reach.
      wisps.setWaves(rings, clock(), fieldAges());
      // Past `MAX_RINGS`, the newest win: the last rings and the last waves' palettes.
      const firstRing = Math.max(0, rings.length - MAX_RINGS);
      const firstPalette = Math.max(0, painting.waves.length - MAX_RINGS);
      ringCount = rings.length - firstRing;
      liveRings.length = 0;
      for (let i = firstRing; i < rings.length; i++) liveRings.push(rings[i]!);
      palettes.setBase(painting.base);
      for (let i = 0; i < MAX_RINGS; i++) {
        const ring = i < ringCount ? rings[firstRing + i] : undefined;
        const palette = ring ? (painting.waves[firstPalette + i] ?? -1) : -1;
        palettes.setRing(i, palette);
        if (!ring) continue;
        ringStrength[i] = ring.amplitude;
      }
      redraw();
    },
  };
}
